import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, CheckCircle, ExternalLink, Phone, Edit, Globe, Shield, Layout, Activity, AlertCircle, Briefcase, StickyNote, Save, Ban } from 'lucide-react';
import { getProspectExtras } from './ProspectExtras';
import { safeUrl } from '../utils/safeUrl';
import { groupPillVars } from '../utils/groupColor';
import useConfirm from '../hooks/useConfirm';
import ResearchPanel from './ResearchPanel';
import './ProspectDetailPanel.css';

const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const initialsOf = (name) => {
  const words = String(name || '').replace(/[^A-Za-z0-9 ]/g, ' ').split(/\s+/).filter(Boolean);
  const out = ((words[0] || '')[0] || '') + ((words[1] || '')[0] || '');
  return out.toUpperCase() || '?';
};

// "https://www.example.com/" -> "www.example.com"
const displayUrl = (url) => String(url || '').replace(/^https?:\/\//i, '').replace(/\/$/, '');

// plain: keep the text as written (no capitalisation), for sentence-like labels
export const Pill = ({ tone = 'neutral', small = false, plain = false, title, children }) => (
  <span className={`pc-pill pc-pill--${tone}${small ? ' pc-pill--sm' : ''}${plain ? ' pc-pill--plain' : ''}`} title={title}>{children}</span>
);

// One group pill tinted with the group's own colour. The colour is data, so it is
// passed inline as CSS custom properties (--group-color, --group-ink); without a
// usable colour the pill keeps the violet tag style.
export const GroupPill = ({ group }) => {
  const vars = groupPillVars(group.color);
  return (
    <span
      className={`pc-pill pc-pill--tag pc-group-pill${vars ? ' pc-group-pill--color' : ''}`}
      style={vars || undefined}
      title={group.name}
    >
      <span className="pc-gdot" aria-hidden="true" />
      <span className="pc-group-name">{group.name}</span>
    </span>
  );
};

// "+N" after the first group: the prospect is in N more groups. The tooltip (hover,
// or focus when focusable) names them; screen readers get the names as text.
// focusable is off inside the clickable prospect row (no focus stop inside a button).
export const GroupMorePill = ({ groups, focusable = false }) => {
  const names = groups.map((g) => g.name).join(', ');
  return (
    <span className="pc-pill pc-pill--neutral pc-pill--sm pc-more" tabIndex={focusable ? 0 : undefined}>
      <span aria-hidden="true">+{groups.length}</span>
      <span className="pc-sr">{`, also in ${groups.length} more group${groups.length === 1 ? '' : 's'}: ${names}`}</span>
      <span className="pc-more-tip" aria-hidden="true">{names}</span>
    </span>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <section className="pd-sec">
    <h3 className="pd-sec-label"><Icon size={13} aria-hidden="true" />{title}</h3>
    {children}
  </section>
);

// Fields the panel can edit: the Edit Buyer modal's fields (business name, industry,
// location, email, phone, status, website, do not contact + reason) plus address and
// notes, which the same buyers update call accepts. Everything else is read-only.
const STATUS_OPTIONS = [
  ['new', 'New'],
  ['contacted', 'Contacted'],
  ['qualified', 'Qualified'],
  ['converted', 'Converted'],
];
const EDIT_FIELDS = [
  'business_name', 'industry', 'location', 'address', 'status',
  'website', 'phone', 'phone_region', 'phone_type_override', 'email',
  'do_not_contact', 'do_not_contact_reason', 'notes',
];
// Country of a phone number typed without a +. Only sent when the owner picks one.
const PHONE_COUNTRIES = [
  ['US', 'United States'], ['GB', 'United Kingdom'], ['CA', 'Canada'], ['AU', 'Australia'],
  ['IE', 'Ireland'], ['NZ', 'New Zealand'], ['ZA', 'South Africa'], ['DE', 'Germany'],
  ['FR', 'France'], ['ES', 'Spain'], ['IT', 'Italy'], ['NL', 'Netherlands'],
];
const PHONE_TYPE_CHOICES = [
  ['', 'Keep as detected'], ['auto', 'Detect again'], ['mobile', 'Mobile (can be texted)'],
  ['landline', 'Landline'], ['voip', 'VoIP'],
];
// Kept exactly as typed (no trimming); every other text field is trimmed on save.
const UNTRIMMED = ['notes', 'do_not_contact_reason'];

const draftFrom = (buyer) => ({
  business_name: buyer.business_name || '',
  industry: buyer.industry || '',
  location: buyer.location || '',
  address: buyer.address || '',
  status: buyer.status || 'new',
  website: buyer.website || '',
  phone: buyer.phone || '',
  phone_region: '',
  phone_type_override: '',
  email: buyer.email || '',
  do_not_contact: !!buyer.do_not_contact,
  do_not_contact_reason: buyer.do_not_contact_reason || '',
  notes: buyer.notes || '',
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Business name is required (as in the modal). Email, phone and website are only
// checked when they were changed, so existing data never blocks a save.
const validateDraft = (draft, initial) => {
  const errors = {};
  if (!draft.business_name.trim()) errors.business_name = 'Business name is required.';
  const email = draft.email.trim();
  if (email && email !== initial.email.trim() && !EMAIL_RE.test(email)) {
    errors.email = 'Enter a valid email address, like name@example.com.';
  }
  const phone = draft.phone.trim();
  if (phone && phone !== initial.phone.trim()
    && (!/^[0-9+().\-\s]+$/.test(phone) || phone.replace(/\D/g, '').length < 6)) {
    errors.phone = 'Enter a phone number using digits, spaces and + ( ) - (at least 6 digits).';
  }
  const website = draft.website.trim();
  if (website && website !== initial.website.trim() && (/\s/.test(website) || !safeUrl(website))) {
    errors.website = 'Enter a web address, like example.com.';
  }
  return errors;
};

// Only the fields that changed. Cleared text fields are sent as null (not '').
const changesFrom = (draft, initial) => {
  const changes = {};
  EDIT_FIELDS.forEach((key) => {
    if (draft[key] === initial[key]) return;
    let value = draft[key];
    if (typeof value === 'string') {
      if (!UNTRIMMED.includes(key)) value = value.trim();
      if (value === '' && key !== 'business_name') value = null;
    }
    changes[key] = value;
  });
  return changes;
};

function ProspectDetailPanel({
  buyer, meta, formatDate, formatDateTime, verifying, verifyNotice,
  onVerify, onCall, onSave, onClose,
}) {
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const editBtnRef = useRef(null);
  const firstFieldRef = useRef(null);
  const wasEditing = useRef(false);
  const dialogBusy = useRef(false);
  const [confirm, confirmDialog] = useConfirm();

  // Do Not Contact from the view mode: marking asks for a reason, removing asks to confirm.
  const [dncOpen, setDncOpen] = useState(false);
  const [researchOpen, setResearchOpen] = useState(false);
  const [dncReason, setDncReason] = useState('');
  const [dncSaving, setDncSaving] = useState(false);
  const [dncError, setDncError] = useState(null);

  const [editing, setEditing] = useState(false);
  const [initial, setInitial] = useState(() => draftFrom(buyer));
  const [draft, setDraft] = useState(() => draftFrom(buyer));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(false);

  const dirty = editing && EDIT_FIELDS.some((key) => draft[key] !== initial[key]);

  // Focus into the panel, lock background scroll, return focus to the card on close.
  useEffect(() => {
    const returnTo = document.activeElement;
    const buyerId = buyer.id;
    if (closeRef.current) closeRef.current.focus();
    document.body.classList.add('prospect-panel-open');
    return () => {
      document.body.classList.remove('prospect-panel-open');
      let target = returnTo && returnTo.isConnected ? returnTo : null;
      if (!target) target = document.querySelector(`[data-prospect-open="${buyerId}"]`);
      if (target && typeof target.focus === 'function') target.focus();
    };
    // Runs once per opened prospect (the panel is keyed by prospect id).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Entering edit mode focuses the first field; leaving it returns focus to Edit.
  useEffect(() => {
    if (editing && !wasEditing.current && firstFieldRef.current) firstFieldRef.current.focus();
    if (!editing && wasEditing.current && editBtnRef.current) editBtnRef.current.focus();
    wasEditing.current = editing;
  }, [editing]);

  const startEdit = () => {
    const snapshot = draftFrom(buyer);
    setInitial(snapshot);
    setDraft(snapshot);
    setErrors({});
    setSaveError(null);
    setSaved(false);
    setEditing(true);
  };

  // Cancel restores the saved values. confirmIfDirty asks before throwing changes away.
  const askConfirm = useCallback(async (options) => {
    dialogBusy.current = true;
    try {
      return await confirm(options);
    } finally {
      dialogBusy.current = false;
    }
  }, [confirm]);

  const cancelEdit = useCallback(async (confirmIfDirty) => {
    if (confirmIfDirty && dirty && !(await askConfirm({
      title: 'Discard changes',
      message: 'Discard your unsaved changes?',
      confirmText: 'Discard',
      cancelText: 'Keep editing'
    }))) return;
    setEditing(false);
    setDraft(initial);
    setErrors({});
    setSaveError(null);
  }, [dirty, initial, askConfirm]);

  const requestClose = useCallback(async () => {
    if (saving || dncSaving || dialogBusy.current || researchOpen) return;
    if (dirty && !(await askConfirm({
      title: 'Discard changes',
      message: 'You have unsaved changes. Discard them and close?',
      confirmText: 'Discard',
      cancelText: 'Keep editing'
    }))) return;
    onClose();
  }, [dirty, saving, dncSaving, researchOpen, onClose, askConfirm]);

  const saveDnc = async (changes) => {
    setDncSaving(true);
    setDncError(null);
    try {
      await onSave(changes);
      setSaved(true);
      setDncOpen(false);
    } catch (err) {
      setDncError((err && err.message) || 'Could not update Do Not Contact.');
    } finally {
      setDncSaving(false);
    }
  };

  const startDnc = async () => {
    if (!buyer.do_not_contact) {
      setDncReason('');
      setDncError(null);
      setDncOpen(true);
      return;
    }
    if (!(await askConfirm({
      title: 'Remove Do Not Contact',
      message: `Allow contacting ${buyer.business_name || 'this prospect'} again?`,
      confirmText: 'Remove',
      variant: 'primary'
    }))) return;
    setSaved(false);
    try {
      await onSave({ do_not_contact: false, do_not_contact_reason: null });
      setSaved(true);
    } catch (err) {
      setDncError((err && err.message) || 'Could not update Do Not Contact.');
      setDncOpen(true);
    }
  };

  // Esc leaves edit mode first; a second Esc closes the panel. Ignored when focus is in
  // something layered on top (for example a confirm dialog).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (dialogBusy.current || dncOpen || researchOpen) return;
      const active = document.activeElement;
      const inPanel = panelRef.current && (panelRef.current.contains(active) || active === document.body || !active);
      if (!inPanel) return;
      e.preventDefault();
      if (saving) return;
      if (editing) cancelEdit(true);
      else onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, editing, saving, cancelEdit, dncOpen, researchOpen]);

  const trapTab = (e) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const items = panelRef.current.querySelectorAll(FOCUSABLE);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const fieldId = (key) => `pd-${buyer.id}-${key}`;
  const errorId = (key) => `${fieldId(key)}-err`;

  const setField = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setDraft((d) => ({ ...d, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const inputProps = (key) => ({
    id: fieldId(key),
    name: key,
    value: draft[key],
    onChange: setField(key),
    disabled: saving,
    className: `pd-input${errors[key] ? ' has-error' : ''}`,
    'aria-invalid': errors[key] ? 'true' : undefined,
    'aria-describedby': errors[key] ? errorId(key) : undefined,
  });

  const fieldError = (key) => (errors[key]
    ? <p className="pd-field-error" id={errorId(key)}>{errors[key]}</p>
    : null);

  const label = (key, text, required = false) => (
    <label htmlFor={fieldId(key)}>
      {text}
      {required && <span className="pd-req" aria-hidden="true"> *</span>}
    </label>
  );

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (saving) return;
    const found = validateDraft(draft, initial);
    setErrors(found);
    const firstBad = EDIT_FIELDS.find((key) => found[key]);
    if (firstBad) {
      const el = document.getElementById(fieldId(firstBad));
      if (el) el.focus();
      return;
    }
    const changes = changesFrom(draft, initial);
    if (Object.keys(changes).length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(changes);
      setEditing(false);
      setSaved(true);
    } catch (err) {
      setSaveError((err && err.message) || 'Could not save the changes.');
    } finally {
      setSaving(false);
    }
  };

  const x = getProspectExtras(buyer);
  const site = x ? x.site : null;
  const groups = Array.isArray(buyer.groups) ? buyer.groups : [];
  const websiteHref = buyer.website ? safeUrl(buyer.website) : null;
  const hasPhone = !!(buyer.phone || buyer.phone_e164);
  const { verificationMeta, phoneMeta, normalizedPhone, phoneValidityState, statusPill, canCall } = meta;
  const lastContact = buyer.last_contact_date ? formatDate(buyer.last_contact_date) : null;
  const lastStatusAt = formatDateTime(buyer.last_contact_status_at);
  const lastVerified = formatDateTime(buyer.last_verified_at);
  const added = buyer.created_at ? formatDate(buyer.created_at) : null;
  const hasSiteDetails = !!site && (
    site.platform || site.year || site.tracking || site.mobileFriendly !== null || site.ageYears !== null
    || x.directory || x.socialOnly
  );
  const titleId = `pd-title-${buyer.id}`;
  const formId = `pd-form-${buyer.id}`;
  const statusOptions = STATUS_OPTIONS.some(([value]) => value === draft.status)
    ? STATUS_OPTIONS
    : [...STATUS_OPTIONS, [draft.status, draft.status]];

  const body = (
    <>
      {editing && (
        <Section icon={Briefcase} title="Business">
          <dl className="pd-kv pd-kv--edit">
            <dt>{label('business_name', 'Business name', true)}</dt>
            <dd>
              <input type="text" {...inputProps('business_name')} ref={firstFieldRef} required autoComplete="off" />
              {fieldError('business_name')}
            </dd>
            <dt>{label('industry', 'Industry')}</dt>
            <dd><input type="text" {...inputProps('industry')} autoComplete="off" /></dd>
            <dt>{label('location', 'Location')}</dt>
            <dd><input type="text" {...inputProps('location')} autoComplete="off" /></dd>
            <dt>{label('address', 'Address')}</dt>
            <dd><input type="text" {...inputProps('address')} autoComplete="off" /></dd>
            <dt>{label('status', 'Lead status')}</dt>
            <dd>
              <select {...inputProps('status')}>
                {statusOptions.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
              </select>
            </dd>
          </dl>
        </Section>
      )}

      <Section icon={Globe} title="Contact & web">
        <dl className={`pd-kv${editing ? ' pd-kv--edit' : ''}`}>
          {editing ? (
            <>
              <dt>{label('website', 'Website')}</dt>
              <dd>
                <input type="text" inputMode="url" placeholder="example.com" {...inputProps('website')} autoComplete="off" />
                {fieldError('website')}
              </dd>
            </>
          ) : (
            <>
              <dt>Website</dt>
              <dd>
                {!buyer.website && <span className="pd-muted">No website</span>}
                {buyer.website && websiteHref && (
                  <a href={websiteHref} target="_blank" rel="noopener noreferrer">{displayUrl(buyer.website)}</a>
                )}
                {buyer.website && !websiteHref && <span>{buyer.website}</span>}
              </dd>
            </>
          )}
          {x && x.contact && (
            <>
              <dt>Contact page</dt>
              <dd><a href={x.contact} target="_blank" rel="noopener noreferrer">{displayUrl(x.contact)}</a></dd>
            </>
          )}
          {editing ? (
            <>
              <dt>{label('phone', 'Phone')}</dt>
              <dd>
                <input type="tel" {...inputProps('phone')} autoComplete="off" />
                {fieldError('phone') || (
                  <p className="pd-field-hint">
                    Add the country code (like +1 or +44) or pick the country below, so the number can be read.
                  </p>
                )}
              </dd>
              <dt>{label('phone_region', 'Phone country')}</dt>
              <dd>
                <select {...inputProps('phone_region')}>
                  <option value="">Detect automatically</option>
                  {PHONE_COUNTRIES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                </select>
              </dd>
              <dt>{label('phone_type_override', 'Phone type')}</dt>
              <dd>
                <select {...inputProps('phone_type_override')}>
                  {PHONE_TYPE_CHOICES.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
                </select>
                <p className="pd-field-hint">
                  Know it is a mobile? Choose Mobile and it can be used for SMS campaigns. The country must be known first.
                </p>
              </dd>
            </>
          ) : (
            <>
              <dt>Phone</dt>
              <dd>
                {hasPhone ? (
                  <span className="pd-inline">
                    <span className="pd-num">{normalizedPhone}</span>
                    <Pill tone="neutral" small>{phoneMeta.label}</Pill>
                    {phoneValidityState === false && !buyer.phone_country && (
                      <Pill tone="warn" small>Country unknown - edit to set it</Pill>
                    )}
                    {phoneValidityState === false && buyer.phone_country && <Pill tone="danger" small>Invalid</Pill>}
                    {buyer.phone_country && <span className="pd-muted">Country: {buyer.phone_country}</span>}
                  </span>
                ) : <span className="pd-muted">No phone</span>}
              </dd>
              {buyer.address && (
                <>
                  <dt>Address</dt>
                  <dd>{buyer.address}</dd>
                </>
              )}
            </>
          )}
          {x && x.socials.length > 0 && (
            <>
              <dt>Socials</dt>
              <dd>
                <span className="pd-chips">
                  {x.socials.map((s) => (
                    <a key={s.platform} className="pd-chip" href={s.url} target="_blank" rel="noopener noreferrer">{s.label}</a>
                  ))}
                </span>
              </dd>
            </>
          )}
        </dl>
      </Section>

      <Section icon={Shield} title="Email health">
        <dl className={`pd-kv${editing ? ' pd-kv--edit' : ''}`}>
          {editing ? (
            <>
              <dt>{label('email', 'Email')}</dt>
              <dd>
                <input type="email" {...inputProps('email')} autoComplete="off" />
                {fieldError('email')}
              </dd>
            </>
          ) : (
            <>
              <dt>Email</dt>
              <dd>{buyer.email ? <span className="pd-break">{buyer.email}</span> : <span className="pd-muted">No email</span>}</dd>
            </>
          )}
          {buyer.email && (
            <>
              <dt>Deliverability</dt>
              <dd><Pill tone={verificationMeta.tone}>{verificationMeta.label}</Pill></dd>
            </>
          )}
          {typeof buyer.email_verification_score === 'number' && (
            <>
              <dt>Score</dt>
              <dd className="pd-num">{buyer.email_verification_score}</dd>
            </>
          )}
          {buyer.email && lastVerified && (
            <>
              <dt>Last checked</dt>
              <dd>{lastVerified}</dd>
            </>
          )}
          {x && x.provider && (
            <>
              <dt>Mail provider</dt>
              <dd>{x.provider}</dd>
            </>
          )}
        </dl>
        {x && x.guessed && (
          <div className="pd-note">
            <AlertCircle size={14} aria-hidden="true" />
            <span><b>Guessed.</b> Not found on their site; common address pattern. Verify before sending.</span>
          </div>
        )}
      </Section>

      <Section icon={Layout} title="Site details">
        {hasSiteDetails ? (
          <dl className="pd-kv">
            {site.platform && (
              <>
                <dt>Platform</dt>
                <dd>{site.platform}</dd>
              </>
            )}
            {site.year && (
              <>
                <dt>Last updated</dt>
                <dd className="pd-inline">
                  <span className="pd-num">{site.year}</span>
                  {site.stale && <Pill tone="warn" small plain title="The footer copyright year is 3 or more years old">Possibly outdated</Pill>}
                </dd>
              </>
            )}
            {site.ageYears !== null && (
              <>
                <dt>Domain age</dt>
                <dd>
                  {site.ageYears < 1 ? 'Under 1 year' : `${site.ageYears} year${site.ageYears === 1 ? '' : 's'}`}
                  {site.created && <span className="pd-muted"> · registered {site.created}</span>}
                </dd>
              </>
            )}
            {site.tracking && (
              <>
                <dt>Tracking</dt>
                <dd>
                  {site.tracking.length === 0 && (
                    <span title="No analytics or ad tags in the page HTML. Tags loaded later by a cookie banner or script may not show.">No tracking seen</span>
                  )}
                  {site.tracking.length > 0 && (site.trackingLabels.length > 0 ? site.trackingLabels.join(', ') : 'Tags detected')}
                </dd>
              </>
            )}
            {site.mobileFriendly !== null && (
              <>
                <dt>Mobile friendly</dt>
                <dd>
                  {site.mobileFriendly
                    ? 'Yes'
                    : <Pill tone="warn" small plain title="No mobile viewport tag found on the homepage">Not mobile friendly</Pill>}
                </dd>
              </>
            )}
            {(x.directory || x.socialOnly) && (
              <>
                <dt>Listed site</dt>
                <dd className="pd-inline">
                  {x.directory && <Pill tone="warn" small plain title="Their listed website is a directory page, not their own site">Directory listing only</Pill>}
                  {x.socialOnly && <Pill tone="warn" small plain title="Their listed website is a social media page, not their own site">Social page only</Pill>}
                </dd>
              </>
            )}
          </dl>
        ) : (
          <p className="pd-empty">No site details collected for this prospect.</p>
        )}
      </Section>

      <Section icon={Activity} title="Activity">
        <dl className={`pd-kv${editing ? ' pd-kv--edit' : ''}`}>
          <dt>Contact</dt>
          <dd>
            {editing ? (
              <div className="pd-dnc-edit">
                <label className="pd-check" htmlFor={fieldId('do_not_contact')}>
                  <input
                    type="checkbox"
                    id={fieldId('do_not_contact')}
                    name="do_not_contact"
                    checked={draft.do_not_contact}
                    onChange={setField('do_not_contact')}
                    disabled={saving}
                  />
                  <span>Do not contact</span>
                </label>
                {draft.do_not_contact && (
                  <>
                    <label className="pd-sublabel" htmlFor={fieldId('do_not_contact_reason')}>Reason</label>
                    <textarea rows={2} {...inputProps('do_not_contact_reason')} />
                  </>
                )}
              </div>
            ) : (
              buyer.do_not_contact ? (
                <span className="pd-inline">
                  <Pill tone="danger">DNC</Pill>
                  <span className="pd-muted">
                    {buyer.do_not_contact_reason || 'No reason given'}
                    {buyer.do_not_contact_date ? ` · ${formatDate(buyer.do_not_contact_date)}` : ''}
                  </span>
                </span>
              ) : <Pill tone="success">Active</Pill>
            )}
          </dd>
          {!editing && (
            <>
              <dt>Lead status</dt>
              <dd><Pill tone="neutral">{buyer.status || 'new'}</Pill></dd>
            </>
          )}
          <dt>Last contact</dt>
          <dd>
            {lastContact ? (
              <span className="pd-inline">
                <span>{lastContact}</span>
                {buyer.last_contact_method && <Pill tone="neutral" small>{buyer.last_contact_method}</Pill>}
              </span>
            ) : <span className="pd-muted">Not contacted yet</span>}
          </dd>
          <dt>Last status</dt>
          <dd>
            {buyer.last_contact_status ? (
              <span className="pd-inline">
                <Pill tone={meta.contactStatusTone}>{buyer.last_contact_status}</Pill>
                {buyer.last_contact_status_method && <span className="pd-muted">{buyer.last_contact_status_method.toUpperCase()}</span>}
                {lastStatusAt && <span className="pd-muted">{lastStatusAt}</span>}
              </span>
            ) : <span className="pd-muted">No attempts</span>}
          </dd>
          <dt>Groups</dt>
          <dd>
            {groups.length > 0 ? (
              <span className="pd-chips">
                {groups.map((g) => <GroupPill key={g.id} group={g} />)}
              </span>
            ) : <span className="pd-muted">No group</span>}
            {editing && <p className="pd-field-hint">Change groups from the Groups menu in the prospects toolbar.</p>}
          </dd>
          {added && (
            <>
              <dt>Added</dt>
              <dd>{added}</dd>
            </>
          )}
        </dl>
      </Section>

      {/* Hidden while editing: the edit form wraps this body and the panel has its own buttons/dialog */}
      {!editing && <ResearchPanel buyerId={buyer.id} onDialogOpenChange={setResearchOpen} />}

      <Section icon={StickyNote} title="Notes">
        {editing ? (
          <>
            <label className="pc-sr" htmlFor={fieldId('notes')}>Notes</label>
            <textarea rows={4} placeholder="Add notes about this prospect" {...inputProps('notes')} />
          </>
        ) : (
          buyer.notes
            ? <p className="pd-notes">{buyer.notes}</p>
            : <p className="pd-empty">No notes yet.</p>
        )}
      </Section>
    </>
  );

  return (
    <div className="pd-root">
      <div className="pd-backdrop" onClick={requestClose} aria-hidden="true" />
      <aside
        className={`pd-panel${editing ? ' is-editing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panelRef}
        onKeyDown={trapTab}
      >
        <header className="pd-head">
          <span className="pd-avatar" aria-hidden="true">{initialsOf(buyer.business_name)}</span>
          <div className="pd-title">
            <h2 id={titleId}>{buyer.business_name || 'Unnamed business'}</h2>
            <span className="pd-sub">
              {buyer.industry || 'No industry'}
              {buyer.location ? ` · ${buyer.location}` : ''}
            </span>
            <div className="pd-badges">
              {editing && <Pill tone="tag" plain>Editing</Pill>}
              <Pill tone={statusPill.tone} title={statusPill.title}>{statusPill.label}</Pill>
              {buyer.email && <Pill tone={verificationMeta.tone}>{verificationMeta.label}</Pill>}
              {groups.length > 0 && <GroupPill group={groups[0]} />}
              {groups.length > 1 && <GroupMorePill groups={groups.slice(1)} focusable />}
            </div>
          </div>
          <button type="button" className="pd-close" ref={closeRef} onClick={requestClose} aria-label="Close details">
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        {editing ? (
          <form id={formId} className="pd-body" onSubmit={handleSave} noValidate aria-label="Edit prospect">
            {body}
          </form>
        ) : (
          <div className="pd-body">{body}</div>
        )}

        {editing && saveError && (
          <div className="pd-status pd-status--error" role="alert">
            <strong>Not saved: </strong>
            {saveError}
          </div>
        )}
        {!editing && verifyNotice && (
          <div className={`pd-status pd-status--${verifyNotice.kind}`} role="status" aria-live="polite">
            {verifyNotice.kind === 'error' && <strong>Error: </strong>}
            {verifyNotice.text}
          </div>
        )}
        {!editing && !verifyNotice && saved && (
          <div className="pd-status pd-status--success" role="status" aria-live="polite">
            Changes saved.
          </div>
        )}

        {editing ? (
          <footer className="pd-foot">
            <button type="submit" form={formId} className="pd-btn pd-btn--primary" disabled={saving}>
              <Save size={15} aria-hidden="true" />
              {saving ? 'Saving...' : 'Save changes'}
            </button>
            <button type="button" className="pd-btn pd-btn--secondary" onClick={() => cancelEdit(false)} disabled={saving}>
              Cancel
            </button>
            {dirty && !saving && <span className="pd-dirty">Unsaved changes</span>}
          </footer>
        ) : (
          <footer className="pd-foot">
            <button
              type="button"
              className="pd-btn pd-btn--primary"
              onClick={() => { setSaved(false); onVerify(); }}
              disabled={!buyer.email || verifying}
              title="Verify email using Hunter.io API"
            >
              <CheckCircle size={15} aria-hidden="true" />
              {verifying ? 'Verifying...' : 'Verify email'}
            </button>
            {buyer.website && (websiteHref ? (
              <a className="pd-btn pd-btn--secondary" href={websiteHref} target="_blank" rel="noopener noreferrer" title={buyer.website}>
                <ExternalLink size={15} aria-hidden="true" />
                Visit website
              </a>
            ) : (
              <span className="pd-btn pd-btn--secondary is-disabled" title={buyer.website}>
                <ExternalLink size={15} aria-hidden="true" />
                Visit website
              </span>
            ))}
            {hasPhone && (
              <button
                type="button"
                className="pd-btn pd-btn--secondary"
                onClick={onCall}
                disabled={!canCall}
                title={canCall ? 'Call from this device' : 'Calling unavailable: phone invalid or DNC'}
              >
                <Phone size={15} aria-hidden="true" />
                Call device
              </button>
            )}
            <button
              type="button"
              className="pd-btn pd-btn--secondary"
              onClick={startDnc}
              disabled={dncSaving}
              title={buyer.do_not_contact ? 'Allow contacting this prospect again' : 'Stop all outreach to this prospect'}
            >
              <Ban size={15} aria-hidden="true" />
              {buyer.do_not_contact ? 'Remove DNC' : 'Mark DNC'}
            </button>
            <button type="button" className="pd-btn pd-btn--secondary" ref={editBtnRef} onClick={startEdit} title="Edit this prospect's details">
              <Edit size={15} aria-hidden="true" />
              Edit
            </button>
          </footer>
        )}
      </aside>
      {confirmDialog}
      {dncOpen && (
        <div className="ui-dialog-overlay ui-dialog-overlay--confirm" onClick={() => !dncSaving && setDncOpen(false)}>
          <div
            className="ui-dialog ui-dialog--confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`pd-dnc-title-${buyer.id}`}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Escape' && !dncSaving) { e.stopPropagation(); setDncOpen(false); } }}
          >
            <div className="ui-dialog-head">
              <div className="ui-dialog-heading">
                <h2 id={`pd-dnc-title-${buyer.id}`} className="ui-dialog-title">Mark as Do Not Contact</h2>
              </div>
              <button type="button" onClick={() => setDncOpen(false)} className="ui-dialog-close" aria-label="Close" disabled={dncSaving}>
                <X size={18} />
              </button>
            </div>
            <div className="ui-dialog-body">
              <p className="ui-dialog-message">
                {buyer.business_name || 'This prospect'} will be skipped by every email and SMS campaign.
              </p>
              <label className="pd-sublabel" htmlFor={`pd-dnc-reason-${buyer.id}`}>Reason</label>
              <textarea
                id={`pd-dnc-reason-${buyer.id}`}
                rows={2}
                className="pd-input"
                value={dncReason}
                onChange={(e) => setDncReason(e.target.value)}
                placeholder="For example: asked not to be contacted"
                disabled={dncSaving}
                autoFocus
              />
              {dncError && <p className="pd-field-error" role="alert">{dncError}</p>}
            </div>
            <div className="ui-dialog-foot">
              <button type="button" onClick={() => setDncOpen(false)} className="btn btn-secondary" disabled={dncSaving}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={dncSaving}
                onClick={() => { setSaved(false); saveDnc({ do_not_contact: true, do_not_contact_reason: dncReason.trim() || 'Manual request' }); }}
              >
                {dncSaving ? 'Saving...' : 'Mark Do Not Contact'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProspectDetailPanel;
